import React, { useEffect, useRef, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, FileText, Warning, CloudArrowDown, ArrowsClockwise, Stethoscope } from "@phosphor-icons/react";
import { toast } from "sonner";
import { RSpinBlock } from "@/components/RSpin";

/**
 * AdminAdMedia — AdMedia is a manually-ingested feed partner.
 *
 * Admin uses this page to:
 *   1. Upload AdMedia's daily CSV report (Date, Advertiser, Campaign,
 *      Sales Amount, IP, AdmediaClickID, PubClickID, PubSubID) — each
 *      row becomes an `actions` document so it flows through the
 *      existing approval + payout pipeline without any new model.
 *   2. See the list of manually-onboarded AdMedia brands at a glance,
 *      with their raw tracking URL (macro-style with `[SUBID]`/`[REF]`/
 *      `[CLICKID]`).
 *
 * Per-brand creation lives inline in the Advertiser Lookup (the same
 * place admin assigns brands), so it doesn't need a duplicate form here.
 */
export default function AdminAdMedia() {
  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const fileInputRef = useRef(null);
  const didFetch = useRef(false);

  // API sync state
  const today = new Date().toISOString().slice(0, 10);
  const threeDaysAgo = new Date(Date.now() - 3 * 86400000).toISOString().slice(0, 10);
  const [syncStart, setSyncStart] = useState(threeDaysAgo);
  const [syncEnd, setSyncEnd] = useState(today);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Backfill + Diagnose state
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [diagInput, setDiagInput] = useState("");
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);

  // Offers feed (redirect_info.php CPC catalog) state
  const [offersSyncing, setOffersSyncing] = useState(false);
  const [offersSyncResult, setOffersSyncResult] = useState(null);
  const [offersPreview, setOffersPreview] = useState(null);
  const [offersPreviewLoading, setOffersPreviewLoading] = useState(false);

  const handleBackfill = async () => {
    if (!window.confirm("Mirror AdMedia CONVERSIONS into the publisher click_log? Non-conversion report clicks are skipped (they double-count real redirects). Safe to re-run.")) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const { data } = await API.post("/admin/admedia/backfill-click-log");
      setBackfillResult(data);
      toast.success(`Backfill done · ${data.upserted} upserted · ${data.modified} updated · ${data.skipped_no_pub_user ?? data.skipped_no_assignment ?? 0} skipped`);
      fetchClicks(onlyConversions, 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const handleForceResync = async () => {
    if (!window.confirm("Force a full AdMedia API re-pull (7 days) + click_log backfill in one shot. Returns proof of what landed in the publisher Click Log. Proceed?")) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const { data } = await API.post("/admin/admedia/force-resync");
      setBackfillResult({ ...data, _force: true });
      if (!data.ok) {
        toast.error(`Force resync failed: ${data.reason || data.error || "unknown"}`);
      } else {
        toast.success(`Force resync done · click_log now has ${data.click_log_admedia_total} AdMedia rows · admedia_clicks ${data.admedia_clicks_total}`);
      }
      fetchClicks(onlyConversions, 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Force resync failed");
    } finally {
      setBackfilling(false);
    }
  };

  const handleDiagnose = async () => {
    const q = diagInput.trim();
    if (!q) { toast.error("Enter a sub_id or email"); return; }
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const params = q.includes("@") ? { email: q } : { sub_id: q };
      const { data } = await API.get("/admin/admedia/diagnose", { params });
      setDiagResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Diagnose failed");
    } finally {
      setDiagLoading(false);
    }
  };

  // Recent clicks panel state
  const [clicks, setClicks] = useState([]);
  const [clicksTotal, setClicksTotal] = useState(0);
  const [conversionsTotal, setConversionsTotal] = useState(0);
  const [clicksLoading, setClicksLoading] = useState(true);
  const [onlyConversions, setOnlyConversions] = useState(false);
  const [clicksPage, setClicksPage] = useState(1);
  const [clicksTotalPages, setClicksTotalPages] = useState(1);
  const [clicksFiltered, setClicksFiltered] = useState(0);
  const CLICKS_PAGE_SIZE = 20;

  const fetchClicks = async (convFilter = onlyConversions, page = clicksPage) => {
    setClicksLoading(true);
    try {
      const { data } = await API.get("/admin/admedia/clicks", {
        params: {
          page,
          page_size: CLICKS_PAGE_SIZE,
          only_conversions: convFilter ? 1 : 0,
        },
      });
      setClicks(data.rows || []);
      setClicksTotal(data.total || 0);
      setConversionsTotal(data.conversions_total || 0);
      setClicksTotalPages(data.total_pages || 1);
      setClicksFiltered(data.total_filtered || 0);
      setClicksPage(data.page || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setClicksLoading(false);
    }
  };

  const fetchBrands = async () => {
    setBrandsLoading(true);
    try {
      const { data } = await API.get("/admin/admedia/brands");
      setBrands(data.brands || []);
    } catch (err) {
      console.error(err);
    } finally {
      setBrandsLoading(false);
    }
  };

  // Inline edit state — keyed by campaign_id so multiple rows can be
  // expanded simultaneously without state collisions. Each entry is the
  // working draft for that row's form fields.
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({
    name: "", advertiser_name: "", tracking_link: "", campaign_url: "",
  });
  const [savingEdit, setSavingEdit] = useState(false);

  const startEdit = (b) => {
    setEditingId(b.campaign_id);
    setEditDraft({
      name: b.name || "",
      advertiser_name: b.advertiser_name || "",
      tracking_link: b.tracking_link || "",
      campaign_url: b.campaign_url || "",
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ name: "", advertiser_name: "", tracking_link: "", campaign_url: "" });
  };
  const saveEdit = async () => {
    if (!editingId) return;
    if (!editDraft.name.trim() || !editDraft.tracking_link.trim()) {
      toast.error("Brand name + tracking URL are both required");
      return;
    }
    if (!editDraft.tracking_link.includes("[SUBID]")) {
      toast.error("Tracking URL must contain the `[SUBID]` macro");
      return;
    }
    setSavingEdit(true);
    try {
      await API.patch(`/admin/brands/admedia/${editingId}`, {
        brand_name: editDraft.name.trim(),
        advertiser_name: editDraft.advertiser_name.trim(),
        tracking_link: editDraft.tracking_link.trim(),
        campaign_url: editDraft.campaign_url.trim(),
      });
      toast.success(`Updated "${editDraft.name}"`);
      cancelEdit();
      fetchBrands();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update brand");
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    fetchBrands();
    fetchClicks(false, 1);
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a .csv file");
      e.target.value = "";
      return;
    }
    setUploading(true);
    setLastResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await API.post("/admin/admedia/upload-report", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastResult(data);
      const ok = data.ingested;
      const dup = data.skipped_duplicate;
      const nopub = data.skipped_no_pub;
      const noassign = data.skipped_no_assignment;
      const msg = `Ingested ${ok} new · ${dup} duplicates · ${nopub} missing PubSubID · ${noassign} no assignment`;
      if (ok > 0) toast.success(msg);
      else toast.info(msg);
      fetchClicks(onlyConversions, 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleApiSync = async () => {
    if (!syncStart || !syncEnd) {
      toast.error("Pick a start and end date");
      return;
    }
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await API.post("/admin/admedia/sync-report", null, {
        params: { start_date: syncStart, end_date: syncEnd },
      });
      setSyncResult(data);
      if (data.skipped) {
        toast.info("AdMedia API not configured — set ADMEDIA_AID / ADMEDIA_AUTH in backend/.env");
      } else {
        const ing = data.ingested || 0;
        const conv = data.conversions_ingested || 0;
        toast.success(`Synced ${ing} click row(s) · ${conv} conversion(s) · ${data.skipped_duplicate || 0} duplicates`);
        fetchClicks(onlyConversions, 1);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "AdMedia API sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncOffers = async () => {
    if (!window.confirm("Fetch the AdMedia offer feed and create/update AdMedia brands from it? Safe to re-run (idempotent).")) return;
    setOffersSyncing(true);
    setOffersSyncResult(null);
    try {
      const { data } = await API.post("/admin/admedia/sync-offers");
      setOffersSyncResult(data);
      if (data.ok) {
        toast.success(`Offers synced · ${data.upserted} new · ${data.modified} updated · ${data.rows_seen} listing(s)`);
        fetchBrands();
      } else {
        toast.info(data.reason || "AdMedia offers sync skipped");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Offers sync failed");
    } finally {
      setOffersSyncing(false);
    }
  };

  const handlePreviewOffers = async () => {
    setOffersPreviewLoading(true);
    try {
      const { data } = await API.get("/admin/admedia/offers");
      setOffersPreview(data);
      if (!data.configured) {
        toast.info("AdMedia API not configured — set ADMEDIA_AID / ADMEDIA_AUTH in backend/.env");
      } else {
        toast.success(`Fetched ${data.total} offer(s) from the feed`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setOffersPreviewLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-admedia-page">
      <div className="mb-6">
        <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-violet-700">
          Feed Partner · Manual
        </p>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">
          AdMedia
        </h1>
        <p className="text-sm text-slate-500 mt-2 max-w-3xl">
          AdMedia has no API — onboard brands manually from the Publisher
          Detail → Advertiser Lookup → AdMedia tab, then upload their daily
          CSV report here. Each row becomes an approved conversion that
          flows through the standard payout pipeline.
        </p>
      </div>

      {/* AdMedia Offers Feed (CPA + CPC catalog) card */}
      <div className="border border-slate-200 rounded-md p-5 mb-6 bg-white" data-testid="admedia-offers-card">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-sky-700">
            Offer feed · redirect_info.php
          </p>
          <span className="text-[10px] font-mono text-slate-400">aid/auth from .env</span>
        </div>
        <h3 className="font-heading text-base font-semibold text-slate-900">
          AdMedia Offers Feed (CPA + CPC catalog)
        </h3>
        <p className="text-xs text-slate-500 mt-1 mb-3 max-w-3xl">
          Pulls AdMedia's <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">/affiliate_links/redirect_info.php</code> offer
          catalog and upserts each listing into <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">brands</code> as an
          assignable <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">network=admedia</code> brand. Offer type is
          auto-detected from the payout — <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">offer_type=cpa</code> for %/$
          commissions, <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">offer_type=cpc</code> for explicit CPC listings.
          Idempotent (keyed on the AdMedia
          campaign id) — re-running keeps existing assignments and admin edits. Tracking links get{" "}
          <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">[SUBID]</code>/<code className="font-mono bg-slate-100 px-1 rounded text-[11px]">[CLICKID]</code>{" "}
          macros injected automatically for attribution.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={handleSyncOffers}
            disabled={offersSyncing}
            className="bg-sky-700 hover:bg-sky-800 text-white"
            data-testid="admedia-offers-sync-trigger"
          >
            <CloudArrowDown size={14} className="mr-2" />
            {offersSyncing ? "Syncing offers…" : "Sync offers → brands"}
          </Button>
          <Button
            onClick={handlePreviewOffers}
            disabled={offersPreviewLoading}
            variant="outline"
            data-testid="admedia-offers-preview-trigger"
          >
            {offersPreviewLoading ? "Fetching…" : "Preview offers"}
          </Button>
          {offersSyncResult?.ok && (
            <div className="text-xs text-slate-600 font-mono" data-testid="admedia-offers-sync-result">
              {offersSyncResult.upserted} new · {offersSyncResult.modified} updated · {offersSyncResult.rows_seen} listing(s)
              {offersSyncResult.skipped ? ` · ${offersSyncResult.skipped} skipped` : ""}
            </div>
          )}
          {offersSyncResult && !offersSyncResult.ok && (
            <div className="text-xs text-amber-700 font-mono" data-testid="admedia-offers-sync-error">
              {offersSyncResult.reason || offersSyncResult.error || "Sync failed"}
            </div>
          )}
        </div>

        {offersPreview && (
          <div className="mt-4 border border-slate-200 rounded-md overflow-hidden" data-testid="admedia-offers-preview">
            {!offersPreview.configured ? (
              <div className="p-3 text-xs text-amber-700 font-mono">
                Not configured — set <code>ADMEDIA_AID</code> / <code>ADMEDIA_AUTH</code> in backend/.env.
              </div>
            ) : offersPreview.total === 0 ? (
              <div className="p-3 text-xs text-slate-500">Feed returned no listings.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/40">
                      <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Advertiser</th>
                      <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Cmp</th>
                      <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Payout</th>
                      <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Geo</th>
                      <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Domain</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offersPreview.offers.map((o, i) => (
                      <tr key={o.cmp || o.advertiser_id || i} className="border-b border-slate-100">
                        <td className="px-4 py-2 text-xs text-slate-800">{o.advertiser_name || "—"}</td>
                        <td className="px-4 py-2 text-[11px] font-mono text-slate-500">{o.cmp || "—"}</td>
                        <td className="px-4 py-2 text-xs text-slate-700">{o.payout || "—"}</td>
                        <td className="px-4 py-2 text-[11px] font-mono text-slate-500">{(o.geo_countries || []).join(", ") || "—"}</td>
                        <td className="px-4 py-2 text-[11px] text-slate-500 truncate max-w-[220px]" title={o.domain || ""}>{o.domain || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* AdMedia API auto-sync card */}
      <div className="border border-slate-200 rounded-md p-5 mb-6 bg-white" data-testid="admedia-sync-card">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-emerald-700">
            Automated · live poll every 5 min + daily safety sweep
          </p>
          <span className="text-[10px] font-mono text-slate-400">live: 5min · daily: 02:30 UTC</span>
        </div>
        <h3 className="font-heading text-base font-semibold text-slate-900">
          Sync from AdMedia API
        </h3>
        <p className="text-xs text-slate-500 mt-1 mb-3">
          Pulls AdMedia's <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">/custom_click_report.php</code> for the
          chosen window. Every row is logged into <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">admedia_clicks</code>;
          rows with <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">Payout &gt; 0</code> or
          <code className="font-mono bg-slate-100 px-1 rounded text-[11px]"> Sales Value &gt; 0</code> are also written as APPROVED
          conversions to <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">actions</code>. Idempotent on ClickId
          (or a synthetic hash when ClickId is empty).
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] font-bold tracking-wider uppercase text-slate-500 mb-1">Start date</label>
            <input
              type="date"
              value={syncStart}
              onChange={(e) => setSyncStart(e.target.value)}
              disabled={syncing}
              className="border border-slate-300 rounded px-2 py-1 text-sm font-mono"
              data-testid="admedia-sync-start"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold tracking-wider uppercase text-slate-500 mb-1">End date</label>
            <input
              type="date"
              value={syncEnd}
              onChange={(e) => setSyncEnd(e.target.value)}
              disabled={syncing}
              className="border border-slate-300 rounded px-2 py-1 text-sm font-mono"
              data-testid="admedia-sync-end"
            />
          </div>
          <Button
            onClick={handleApiSync}
            disabled={syncing}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="admedia-sync-trigger"
          >
            <CloudArrowDown size={14} className="mr-2" />
            {syncing ? "Syncing…" : "Sync from AdMedia API"}
          </Button>
          {syncResult && !syncResult.skipped && (
            <div className="text-xs text-slate-600 font-mono" data-testid="admedia-sync-result">
              {syncResult.ingested} click(s) · {syncResult.conversions_ingested || 0} conv · {syncResult.skipped_duplicate || 0} dup · {syncResult.pages_fetched} page(s)
            </div>
          )}
          {syncResult?.skipped && (
            <div className="text-xs text-amber-700 font-mono" data-testid="admedia-sync-not-configured">
              Not configured — set <code>ADMEDIA_AID</code> / <code>ADMEDIA_AUTH</code> in backend/.env.
            </div>
          )}
        </div>
      </div>

      {/* CSV upload card */}
      <div className="border border-slate-200 rounded-md p-5 mb-6 bg-white" data-testid="admedia-upload-card">
        <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-slate-500 mb-2">
          Daily report upload
        </p>
        <h3 className="font-heading text-base font-semibold text-slate-900">
          Upload AdMedia CSV
        </h3>
        <p className="text-xs text-slate-500 mt-1 mb-3">
          Expected columns:{" "}
          <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">
            Date, Advertiser, Campaign, Sales Amount, IP Address, AdmediaClickID, PubClickID, PubSubID
          </code>
          . Order doesn't matter; column names are case-insensitive. Re-uploading the same report is safe (idempotent on AdmediaClickID).
        </p>
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleUpload}
            disabled={uploading}
            className="hidden"
            id="admedia-csv-input"
            data-testid="admedia-csv-input"
          />
          <label htmlFor="admedia-csv-input">
            <Button
              asChild
              disabled={uploading}
              className="bg-violet-600 hover:bg-violet-700 text-white cursor-pointer"
              data-testid="admedia-csv-trigger"
            >
              <span>
                <Upload size={14} className="mr-2" />
                {uploading ? "Uploading…" : "Choose CSV file"}
              </span>
            </Button>
          </label>
          {lastResult && (
            <div className="text-xs text-slate-600 font-mono" data-testid="admedia-upload-result">
              Last upload · {lastResult.ingested} ingested
              {lastResult.skipped_duplicate ? ` · ${lastResult.skipped_duplicate} dup` : ""}
              {lastResult.skipped_no_pub ? ` · ${lastResult.skipped_no_pub} no-pub` : ""}
              {lastResult.skipped_no_assignment ? ` · ${lastResult.skipped_no_assignment} no-assignment` : ""}
            </div>
          )}
        </div>

        {/* Show first few skip details if any */}
        {lastResult?.skip_details?.length > 0 && (
          <div className="mt-4 border border-amber-200 bg-amber-50/40 rounded-md p-3" data-testid="admedia-skip-details">
            <div className="flex items-center gap-2 mb-2">
              <Warning size={14} className="text-amber-600" weight="bold" />
              <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700">
                Skipped rows ({lastResult.skip_details.length})
              </p>
            </div>
            <ul className="space-y-1 text-xs text-slate-700">
              {lastResult.skip_details.slice(0, 10).map((s, i) => (
                <li key={i} className="font-mono text-[11px]">
                  · row {s.row}: {s.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Pub click-log diagnostics card */}
      <div className="border border-amber-200 rounded-md p-5 mb-6 bg-amber-50/30" data-testid="admedia-diag-card">
        <div className="flex items-center gap-2 mb-2">
          <Stethoscope size={16} className="text-amber-700" weight="bold" />
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-amber-700">
            Pub click-log diagnostics
          </p>
        </div>
        <h3 className="font-heading text-base font-semibold text-slate-900">
          AdMedia rows missing from a pub's Click Log?
        </h3>
        <p className="text-xs text-slate-600 mt-1 mb-3 max-w-3xl">
          Two things to check: (1) hit "Backfill click_log" once after deploy so historical AdMedia <b>conversions</b> propagate into Click Log (non-conversion report rows are not mirrored — they double-count real redirects). (2) Use the diagnose box to confirm a specific publisher's wiring (assignment + subid match + mirror count).
        </p>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Button
            onClick={handleBackfill}
            disabled={backfilling}
            className="bg-amber-700 hover:bg-amber-800 text-white"
            data-testid="admedia-backfill-trigger"
          >
            <ArrowsClockwise size={14} className="mr-2" />
            {backfilling ? "Working…" : "Backfill click_log"}
          </Button>
          <Button
            onClick={handleForceResync}
            disabled={backfilling}
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="admedia-force-resync-trigger"
          >
            <ArrowsClockwise size={14} className="mr-2" weight="bold" />
            {backfilling ? "Working…" : "Force resync (7d + backfill)"}
          </Button>
          {backfillResult && !backfillResult._force && (
            <div className="text-xs text-slate-600 font-mono" data-testid="admedia-backfill-result">
              upserted={backfillResult.upserted} · updated={backfillResult.modified} · unassigned-written={backfillResult.unassigned_but_written ?? 0} · skipped (no user)={backfillResult.skipped_no_pub_user ?? backfillResult.skipped_no_assignment ?? 0}
            </div>
          )}
          {backfillResult && backfillResult._force && backfillResult.ok && (
            <div className="text-xs text-slate-700 font-mono space-y-0.5" data-testid="admedia-force-result">
              <div>API sync: pages={backfillResult.sync?.pages_fetched} rows={backfillResult.sync?.rows_seen} ingested={backfillResult.sync?.ingested} click_log={backfillResult.sync?.click_log_ingested}</div>
              <div>Backfill mirror: upserted={backfillResult.backfill?.upserted} updated={backfillResult.backfill?.modified}</div>
              <div className="text-emerald-700"><b>Now in click_log: {backfillResult.click_log_admedia_total} AdMedia rows</b></div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <label className="block text-[10px] font-bold tracking-wider uppercase text-slate-500 mb-1">Publisher sub_id or email</label>
            <input
              type="text"
              value={diagInput}
              onChange={(e) => setDiagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleDiagnose(); }}
              placeholder="e.g. 4WLVM3  or  test4wlvm3@redclub.com"
              className="border border-slate-300 rounded px-2 py-1 text-sm font-mono w-full"
              data-testid="admedia-diag-input"
            />
          </div>
          <Button onClick={handleDiagnose} disabled={diagLoading} variant="outline" data-testid="admedia-diag-trigger">
            {diagLoading ? "Diagnosing…" : "Diagnose"}
          </Button>
        </div>
        {diagResult && (
          <div className="mt-4 border border-slate-200 rounded bg-white p-3 text-xs" data-testid="admedia-diag-result">
            {!diagResult.ok ? (
              <div className="text-rose-700">
                {diagResult.reason} ·  globally: admedia_clicks={diagResult.global?.admedia_clicks_total ?? 0}, click_log mirrored={diagResult.global?.click_log_admedia_total ?? 0}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-x-6 gap-y-1 font-mono text-slate-700">
                  <span><b>pub:</b> {diagResult.publisher.email || "—"} ({diagResult.publisher.sub_id || "no sub_id"})</span>
                  <span><b>admedia_clicks (by pub_id):</b> {diagResult.counts.admedia_clicks_by_publisher_id}</span>
                  <span><b>admedia_clicks (by sub_id):</b> {diagResult.counts.admedia_clicks_by_sub_id}</span>
                  <span><b>click_log mirrored:</b> {diagResult.counts.click_log_admedia_rows}</span>
                </div>
                {diagResult.admedia_assignments ? (
                  <div className="text-slate-700">
                    <b>AdMedia assignments:</b>
                    <ul className="ml-4 list-disc">
                      {diagResult.admedia_assignments.map((a, i) => (
                        <li key={i} className="font-mono">
                          {a.brand_name} ({a.brand_id}) · {a.custom_commission_pct}% · split {a.pub_split_pct}/80 · {a.status}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="text-rose-700 font-mono">No AdMedia assignments for this pub.</div>
                )}
                {diagResult.hints?.length > 0 && (
                  <div className="border-l-2 border-amber-400 pl-2 mt-2">
                    <b className="text-amber-700">Hints:</b>
                    <ul className="list-disc ml-4 text-slate-700">
                      {diagResult.hints.map((h, i) => <li key={i}>{h}</li>)}
                    </ul>
                  </div>
                )}
                <details className="mt-2">
                  <summary className="cursor-pointer text-slate-500 text-[11px]">Sample rows + global totals</summary>
                  <pre className="text-[10px] mt-2 p-2 bg-slate-50 rounded overflow-x-auto">{JSON.stringify({
                    sample_admedia_click: diagResult.sample_admedia_click,
                    sample_click_log_row: diagResult.sample_click_log_row,
                    global: diagResult.global,
                  }, null, 2)}</pre>
                </details>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recent clicks panel */}
      <div className="border border-slate-200 rounded-md bg-white mb-6" data-testid="admedia-clicks-card">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-slate-500">
              Synced data · admedia_clicks
            </p>
            <h3 className="font-heading text-base font-semibold text-slate-900">
              Recent clicks
              <span className="ml-2 text-xs font-mono text-slate-500">
                {clicksTotal.toLocaleString()} total · {conversionsTotal.toLocaleString()} conversions
              </span>
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none" data-testid="admedia-clicks-only-conv-toggle">
              <input
                type="checkbox"
                checked={onlyConversions}
                onChange={(e) => { setOnlyConversions(e.target.checked); fetchClicks(e.target.checked, 1); }}
                className="rounded"
              />
              Only conversions (Payout&nbsp;&gt;&nbsp;0)
            </label>
            <Button variant="outline" size="sm" onClick={() => fetchClicks(onlyConversions, 1)} disabled={clicksLoading} data-testid="admedia-clicks-refresh">
              Refresh
            </Button>
          </div>
        </div>
        {clicksLoading ? (
          <RSpinBlock label="Loading clicks" height={180} />
        ) : clicks.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">
            <FileText size={28} className="mx-auto text-slate-300 mb-2" />
            No clicks ingested yet — run an API sync or upload a CSV above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admedia-clicks-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/40">
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Date</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Advertiser</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Brand</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">SubID</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">ClickID</th>
                  <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Payout</th>
                  <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Sales</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Type</th>
                </tr>
              </thead>
              <tbody>
                {clicks.map((c) => (
                  <tr key={c.idem_key} className="border-b border-slate-100" data-testid={`admedia-click-row-${c.idem_key}`}>
                    <td className="px-4 py-2 text-[11px] font-mono text-slate-700 whitespace-nowrap">{c.event_at || "—"}</td>
                    <td className="px-4 py-2 text-xs text-slate-700">{c.advertiser || "—"}</td>
                    <td className="px-4 py-2 text-xs">
                      <div className="text-slate-900">{c.brand_name || <span className="text-slate-400">unmapped</span>}</div>
                      {c.admedia_campaign_id && (
                        <div className="text-[10px] font-mono text-slate-400">cmp · {c.admedia_campaign_id}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-[11px] font-mono text-slate-600">{c.pub_sub_id || <span className="text-amber-600">—</span>}</td>
                    <td className="px-4 py-2 text-[10px] font-mono text-slate-500 max-w-[180px] truncate" title={c.click_id || c.idem_key}>
                      {c.click_id || <span className="text-slate-400 italic">synth</span>}
                    </td>
                    <td className="px-4 py-2 text-xs font-mono text-right">
                      {c.payout > 0 ? <span className="text-emerald-700 font-semibold">${c.payout.toFixed(2)}</span> : <span className="text-slate-300">$0.00</span>}
                    </td>
                    <td className="px-4 py-2 text-xs font-mono text-right">
                      {c.sales_value > 0 ? <span className="text-emerald-700">${c.sales_value.toFixed(2)}</span> : <span className="text-slate-300">$0.00</span>}
                    </td>
                    <td className="px-4 py-2">
                      {c.is_conversion ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider">Conversion</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wider">Click</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-2.5 text-[11px] text-slate-600 border-t border-slate-100 flex items-center justify-between bg-slate-50/40" data-testid="admedia-clicks-pagination">
              <span className="font-mono">
                {((clicksPage - 1) * CLICKS_PAGE_SIZE) + 1}–{Math.min(clicksPage * CLICKS_PAGE_SIZE, clicksFiltered)} of {clicksFiltered.toLocaleString()}
                {onlyConversions ? " (conversions)" : ""}
                {clicksTotal !== clicksFiltered ? ` · ${clicksTotal.toLocaleString()} total` : ""}
              </span>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={clicksPage <= 1 || clicksLoading} onClick={() => fetchClicks(onlyConversions, clicksPage - 1)} data-testid="admedia-clicks-prev">
                  Prev
                </Button>
                <span className="font-mono mx-2">Page {clicksPage} / {Math.max(1, clicksTotalPages)}</span>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={clicksPage >= clicksTotalPages || clicksLoading} onClick={() => fetchClicks(onlyConversions, clicksPage + 1)} data-testid="admedia-clicks-next">
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Brands list */}
      <div className="border border-slate-200 rounded-md bg-white" data-testid="admedia-brands-card">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-slate-500">
              Manual brands
            </p>
            <h3 className="font-heading text-base font-semibold text-slate-900">
              All AdMedia brands ({brands.length})
            </h3>
          </div>
          <Button variant="outline" size="sm" onClick={fetchBrands} disabled={brandsLoading} data-testid="admedia-brands-refresh">
            Refresh
          </Button>
        </div>
        {brandsLoading ? (
          <RSpinBlock label="Loading AdMedia brands" height={180} />
        ) : brands.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">
            <FileText size={28} className="mx-auto text-slate-300 mb-2" />
            No AdMedia brands yet. Onboard one from Publisher Detail → Advertiser Lookup → AdMedia tab.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/40">
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Brand</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Tracking URL</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Created</th>
                  <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {brands.map((b) => (
                  <React.Fragment key={b.campaign_id}>
                    <tr className="border-b border-slate-100" data-testid={`admedia-brand-row-${b.campaign_id}`}>
                      <td className="px-4 py-2.5">
                        <div className="text-sm font-medium text-slate-900">{b.name}</div>
                        {b.advertiser_name && b.advertiser_name !== b.name && (
                          <div className="text-[10px] text-slate-400">{b.advertiser_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <code className="font-mono text-[10px] text-slate-600 break-all">
                          {b.tracking_link}
                        </code>
                      </td>
                      <td className="px-4 py-2.5 text-[10px] text-slate-500 font-mono whitespace-nowrap">
                        {b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}
                        {b.updated_at && (
                          <div className="text-amber-600">Edited {new Date(b.updated_at).toLocaleDateString()}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {editingId === b.campaign_id ? (
                          <span className="text-[10px] text-violet-600 font-semibold">EDITING</span>
                        ) : (
                          <Button
                            size="sm" variant="outline" className="h-7 text-[11px]"
                            onClick={() => startEdit(b)}
                            data-testid={`admedia-brand-edit-${b.campaign_id}`}
                          >
                            Edit
                          </Button>
                        )}
                      </td>
                    </tr>
                    {editingId === b.campaign_id && (
                      <tr className="bg-violet-50/30 border-b border-slate-100">
                        <td colSpan={4} className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-3" data-testid={`admedia-brand-edit-row-${b.campaign_id}`}>
                            <div className="md:col-span-3 space-y-1">
                              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Brand name</Label>
                              <Input
                                value={editDraft.name}
                                onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                                className="h-9 text-sm"
                                data-testid={`admedia-edit-name-${b.campaign_id}`}
                              />
                            </div>
                            <div className="md:col-span-3 space-y-1">
                              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Advertiser name</Label>
                              <Input
                                value={editDraft.advertiser_name}
                                onChange={(e) => setEditDraft({ ...editDraft, advertiser_name: e.target.value })}
                                className="h-9 text-xs"
                                placeholder="(optional, defaults to brand)"
                                data-testid={`admedia-edit-advertiser-${b.campaign_id}`}
                              />
                            </div>
                            <div className="md:col-span-6 space-y-1">
                              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Tracking URL <span className="text-slate-400 font-normal normal-case">(must keep [SUBID])</span></Label>
                              <Input
                                value={editDraft.tracking_link}
                                onChange={(e) => setEditDraft({ ...editDraft, tracking_link: e.target.value })}
                                className="h-9 text-xs font-mono"
                                data-testid={`admedia-edit-tracking-${b.campaign_id}`}
                              />
                              {editDraft.tracking_link && !editDraft.tracking_link.includes("[SUBID]") && (
                                <p className="text-[10px] text-rose-600">Missing `[SUBID]` macro — required for attribution.</p>
                              )}
                            </div>
                            <div className="md:col-span-9 space-y-1">
                              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Landing URL (optional)</Label>
                              <Input
                                value={editDraft.campaign_url}
                                onChange={(e) => setEditDraft({ ...editDraft, campaign_url: e.target.value })}
                                className="h-9 text-xs"
                                data-testid={`admedia-edit-landing-${b.campaign_id}`}
                              />
                            </div>
                            <div className="md:col-span-3 flex items-end gap-2">
                              <Button
                                onClick={saveEdit}
                                disabled={savingEdit || !editDraft.name.trim() || !editDraft.tracking_link.trim() || !editDraft.tracking_link.includes("[SUBID]")}
                                className="h-9 flex-1 bg-violet-600 hover:bg-violet-700 text-white text-xs"
                                data-testid={`admedia-edit-save-${b.campaign_id}`}
                              >
                                {savingEdit ? "Saving…" : "Save"}
                              </Button>
                              <Button
                                onClick={cancelEdit}
                                disabled={savingEdit}
                                variant="outline"
                                className="h-9 text-xs"
                                data-testid={`admedia-edit-cancel-${b.campaign_id}`}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
